import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { FaRegComment } from "react-icons/fa6";
import { updateCommentState } from "../../api/reportsApi";
import { ReviewComment } from "../../types/Rows";
import { Modal, ModalBody, ModalHeader } from "../ui/Modal";

export default function CommentsModal({
  comments,
  addItem,
  reportId,
}: {
  comments: ReviewComment[];
  addItem: (val: string) => void;
  reportId: string;
}) {
  let [isOpen, setIsOpen] = useState(false);
  let [value, setValue] = useState<string>("");
  const queryClient = useQueryClient();

  function closeModal() {
    setIsOpen(false);
  }

  const { mutate: updateState } = useMutation(
    async (val: number) =>
      await updateCommentState(reportId, comments[0].id, val),
    {
      onSuccess: () => {
        queryClient.resetQueries(["Discussion"]);
      },
    }
  );

  function openModal() {
    setIsOpen(true);
  }

  const sortedComments = comments.sort((a, b) =>
    new Date(a.created) < new Date(b.created) ? -1 : 1
  );

  return (
    <>
      <div className="inset-0 flex items-center justify-center">
        <button type="button" onClick={openModal} className="flex items-center gap-1 text-sm">
          <FaRegComment
            color={sortedComments[0]?.state === 0 ? "#F4A832" : "#6B6B68"}
          />
          {comments.length > 0 && (
            <span className="text-xs text-[var(--sc-muted)]">{comments.length}</span>
          )}
        </button>
      </div>

      <Modal open={isOpen} onClose={closeModal}>
        <ModalHeader title="Komentarze" onClose={closeModal} />
        <ModalBody>
          <div className="space-y-3 mb-4">
            {sortedComments.map((comment) => (
              <div key={comment.id} className="rounded-lg border border-[var(--sc-border)] px-3 py-2.5">
                <p className="text-xs font-semibold text-[var(--sc-text)] mb-0.5">
                  {comment.createdBy}{" "}
                  <span className="font-normal text-[var(--sc-muted)]">
                    {new Date(comment.created).toLocaleDateString()}{" "}
                    {new Date(comment.created).toLocaleTimeString()}
                  </span>
                </p>
                <p className="text-sm text-[var(--sc-muted)]">{comment.comment}</p>
              </div>
            ))}
          </div>

          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full rounded-lg border border-[var(--sc-border)] px-3 py-2 text-sm text-[var(--sc-text)] placeholder:text-[var(--sc-muted)] focus:outline-none focus:ring-1 focus:ring-[var(--sc-amber)]"
            rows={3}
            placeholder="Dodaj komentarz..."
          />

          <div className="flex items-center justify-between gap-2 pt-3">
            <button
              type="button"
              className="rounded-full px-3 py-1.5 text-sm font-medium bg-[var(--sc-bg)] text-[var(--sc-muted)] hover:bg-[var(--sc-border)] transition-colors"
              onClick={closeModal}
            >
              Zamknij
            </button>
            <div className="flex gap-2">
              {sortedComments.length > 0 && (
                <button
                  type="button"
                  className="rounded-full px-3 py-1.5 text-sm font-medium bg-[#EDFAF3] text-[#1E8A55] hover:bg-[#d8f5e9] transition-colors"
                  onClick={async () => {
                    await updateState(1);
                    closeModal();
                    setValue("");
                  }}
                >
                  Oznacz jako rozwiązane
                </button>
              )}
              <button
                type="button"
                className="rounded-full px-3 py-1.5 text-sm font-medium bg-[#F4A832] text-[#0F0F0F] hover:bg-[#e09620] transition-colors"
                onClick={async () => {
                  if (sortedComments) await updateState(0);
                  await addItem(value);
                  setValue("");
                }}
              >
                Dodaj komentarz
              </button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}
